/* eslint-disable react-refresh/only-export-components */
import { Toast as ToastPrimitive } from '@base-ui/react/toast';
import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
  XIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type ToastVariant = 'loading' | 'info' | 'success' | 'warning' | 'error';

type ToastData = {
  variant: ToastVariant;
};

const toast = ToastPrimitive.createToastManager<ToastData>();

function ToastProvider(props: ToastPrimitive.Provider.Props) {
  return <ToastPrimitive.Provider {...props} />;
}

function ToastPortal(props: ToastPrimitive.Portal.Props) {
  return <ToastPrimitive.Portal data-slot="toast-portal" {...props} />;
}

function ToastViewport({ className, ...props }: ToastPrimitive.Viewport.Props) {
  return (
    <ToastPrimitive.Viewport
      data-slot="toast-viewport"
      // z-[1300] rather than z-50 because PrimeReact stacks its Dialog at 1101+ and its own
      // Toast at 1200. At z-50 these toasts render behind any open PrimeReact dialog, which
      // hid the upload progress toast completely.
      className={cn(
        'pointer-events-none fixed inset-x-4 top-4 z-[1300] mx-auto w-auto max-w-sm outline-none sm:right-4 sm:left-auto sm:mx-0 sm:w-full',
        className,
      )}
      {...props}
    />
  );
}

function Toast({ className, ...props }: ToastPrimitive.Root.Props) {
  return (
    <ToastPrimitive.Root
      data-slot="toast"
      className={cn(
        'group/toast bg-popover text-popover-foreground focus-visible:border-ring focus-visible:ring-ring/50 pointer-events-auto absolute top-0 right-0 z-[calc(1000-var(--toast-index))] w-full origin-top rounded-2xl border shadow-lg will-change-transform outline-none select-none focus-visible:ring-[3px]',
        'data-[type=error]:border-destructive data-[type=error]:text-destructive-foreground data-[type=info]:border-info data-[type=info]:text-info-foreground data-[type=success]:border-success data-[type=success]:text-success-foreground data-[type=warning]:border-warning data-[type=warning]:text-warning-foreground data-[type=error]:bg-[color-mix(in_oklab,var(--destructive)_10%,white)] data-[type=info]:bg-[color-mix(in_oklab,var(--info)_10%,white)] data-[type=success]:bg-[color-mix(in_oklab,var(--success)_10%,white)] data-[type=warning]:bg-[color-mix(in_oklab,var(--warning)_10%,white)] dark:data-[type=error]:bg-[color-mix(in_oklab,var(--destructive)_20%,black)] dark:data-[type=info]:bg-[color-mix(in_oklab,var(--info)_20%,black)] dark:data-[type=success]:bg-[color-mix(in_oklab,var(--success)_20%,black)] dark:data-[type=warning]:bg-[color-mix(in_oklab,var(--warning)_20%,black)]',
        '[--gap:0.75rem] [--height:var(--toast-frontmost-height,var(--toast-height))] [--offset-y:calc(var(--toast-offset-y)+calc(var(--toast-index)*var(--gap))+var(--toast-swipe-movement-y))] [--peek:0.75rem] [--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]',
        'h-(--height) [transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--peek))+(var(--shrink)*var(--height))))_scale(var(--scale))] [transition:transform_500ms_cubic-bezier(0.22,1,0.36,1),opacity_500ms,height_150ms]',
        "after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']",
        'data-expanded:h-(--toast-height) data-expanded:[transform:translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))]',
        'data-limited:opacity-0 data-starting-style:[transform:translateY(-150%)]',
        '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(-150%)]',
        'data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
        'data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
        'data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
        'data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
        'data-expanded:data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
        'data-expanded:data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
        'data-expanded:data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
        'data-expanded:data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
        className,
      )}
      {...props}
    />
  );
}

function ToastContent({ className, ...props }: ToastPrimitive.Content.Props) {
  return (
    <ToastPrimitive.Content
      data-slot="toast-content"
      className={cn(
        'flex h-full items-center gap-3 overflow-hidden p-4 transition-opacity duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] data-behind:opacity-0 data-expanded:opacity-100',
        className,
      )}
      {...props}
    />
  );
}

function ToastTitle({ className, ...props }: ToastPrimitive.Title.Props) {
  return (
    <ToastPrimitive.Title
      data-slot="toast-title"
      className={cn('m-0 text-sm font-medium', className)}
      {...props}
    />
  );
}

function ToastDescription({
  className,
  ...props
}: ToastPrimitive.Description.Props) {
  return (
    <ToastPrimitive.Description
      data-slot="toast-description"
      className={cn('m-0 text-sm text-current', className)}
      {...props}
    />
  );
}

function ToastAction({
  className,
  render = <Button variant="outline" size="sm" />,
  ...props
}: ToastPrimitive.Action.Props) {
  return (
    <ToastPrimitive.Action
      data-slot="toast-action"
      render={render}
      className={cn('shrink-0', className)}
      {...props}
    />
  );
}

function ToastClose({
  className,
  children,
  render = <Button variant="ghost" size="icon-sm" />,
  ...props
}: ToastPrimitive.Close.Props) {
  return (
    <ToastPrimitive.Close
      data-slot="toast-close"
      aria-label="Close toast"
      render={render}
      className={cn(
        "text-muted-foreground hover:text-foreground relative shrink-0 bg-transparent after:absolute after:-inset-2 after:content-[''] hover:bg-transparent",
        className,
      )}
      {...props}
    >
      {children ?? <XIcon aria-hidden="true" />}
    </ToastPrimitive.Close>
  );
}

function ToastIcon({ type }: { type: ToastVariant | undefined }) {
  const Icon =
    type === 'success'
      ? CircleCheckIcon
      : type === 'info'
        ? InfoIcon
        : type === 'warning'
          ? TriangleAlertIcon
          : type === 'error'
            ? OctagonXIcon
            : type === 'loading'
              ? Loader2Icon
              : null;

  if (!Icon) {
    return null;
  }

  return (
    <Icon
      data-slot="toast-icon"
      className={cn(
        'size-4 shrink-0',
        type === 'loading' && 'animate-spin',
        type === 'info' && 'text-info',
        type === 'success' && 'text-success',
        type === 'warning' && 'text-warning',
        type === 'error' && 'text-destructive',
      )}
      aria-hidden="true"
    />
  );
}

function ToastList() {
  const { toasts } = ToastPrimitive.useToastManager<ToastData>();

  return toasts.map((toastItem) => {
    const variant = toastItem.data?.variant;

    return (
      <Toast
        key={toastItem.id}
        toast={{ ...toastItem, type: variant }}
        swipeDirection={[]}
      >
        <ToastContent>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-center gap-3">
              <ToastIcon type={variant} />
              <ToastTitle className="min-w-0 flex-1" />
              <ToastAction />
              {variant !== 'loading' && <ToastClose />}
            </div>
            <ToastDescription className="min-w-0 px-7" />
          </div>
        </ToastContent>
      </Toast>
    );
  });
}

function Toaster({
  children,
  toastManager = toast,
  ...props
}: ToastPrimitive.Provider.Props) {
  return (
    <ToastProvider toastManager={toastManager} {...props}>
      {children}
      <ToastPortal>
        <ToastViewport>
          <ToastList />
        </ToastViewport>
      </ToastPortal>
    </ToastProvider>
  );
}

const createToastManager = ToastPrimitive.createToastManager;
const useToastManager = ToastPrimitive.useToastManager;

export {
  Toaster,
  Toast,
  ToastAction,
  ToastClose,
  ToastContent,
  ToastDescription,
  ToastPortal,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  createToastManager,
  toast,
  useToastManager,
};

export type { ToastVariant };
